import { Suspense, useContext, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { HelmetProvider } from 'react-helmet-async';
import { SsrRouteContext } from './prerender';
import { FeedbackProvider } from '@/context/FeedbackContext';
import { ActorProvider, initTracking } from '@/analytics';
import { RedactedPageViews } from '@/analytics/RedactedPageViews';
import { EventInspector } from '@/components/dev/EventInspector';
import { VersionBadge } from '@/components/app/VersionBadge';
import { UpdateNotice } from '@/components/app/UpdateNotice';
// LandingPage and other prerendered pages are imported eagerly (not lazy) so their
// routes render real content during SSG instead of a Suspense fallback.
import { LandingPage } from '@/pages/LandingPage';
import { BluebeamForMacPage } from '@/pages/BluebeamForMacPage';
import { BluebeamRevuFreeAlternativePage } from '@/pages/BluebeamRevuFreeAlternativePage';
import { BluebeamRevu20EndOfLifePage } from '@/pages/BluebeamRevu20EndOfLifePage';
import { MeasurePdfOnlinePage } from '@/pages/MeasurePdfOnlinePage';
import { DrawingScalesPage } from '@/pages/DrawingScalesPage';
import { MeasureAreaPdfPage } from '@/pages/MeasureAreaPdfPage';
import { CalibratePdfScalePage } from '@/pages/CalibratePdfScalePage';
import { SharePdfDrawingsForReviewPage } from '@/pages/SharePdfDrawingsForReviewPage';
import { IpadPdfMarkupPage } from '@/pages/IpadPdfMarkupPage';
import { RedlinePdfDrawingPage } from '@/pages/RedlinePdfDrawingPage';
import { WhatsNewPage } from '@/pages/WhatsNewPage';
import { FreePdfMarkupToolsPage } from '@/pages/FreePdfMarkupToolsPage';
import { DeLandingPage } from '@/pages/DeLandingPage';
import { FrLandingPage } from '@/pages/FrLandingPage';
import { DePdfMessenPage } from '@/pages/DePdfMessenPage';
import { FrMesurerPdfPage } from '@/pages/FrMesurerPdfPage';
import { ChunkErrorBoundary } from '@/components/app/ChunkErrorBoundary';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { LoadingSpinner } from '@/components/ui/primitives';
import { SiteHeader } from '@/components/marketing/SiteHeader';

// Fetch /version.json once so tracked events carry the deployed app_version.
// No-op under SSR (guarded inside initTracking).
if (!import.meta.env.SSR) {
    initTracking();
}

const LocalViewerPage = lazyWithRetry(() => import('@/pages/LocalViewerPage').then(m => ({ default: m.LocalViewerPage })));
const CloudApp = lazyWithRetry(() => import('./CloudApp'));
// Hidden device probe (slice I0 of spec--pdfmarkuptool-image-markup): not linked from anywhere, lazy so
// it adds nothing to the initial chunk (scripts/check-bundle-size.mjs asserts it).
const ImageProbePage = lazyWithRetry(() => import('@/features/probe/ImageProbePage').then(m => ({ default: m.ImageProbePage })));

const LoadingScreen = () => (
    <div className="app-loading">
        <LoadingSpinner />
        <p>Loading...</p>
    </div>
);

/** The cloud routes, with the site top bar over /login (a way home from Sign in). The bar sits outside the lazy cloud
 *  chunk, so it shows at once -- also when the build has no Firebase config. */
function CloudRoute() {
    const onLogin = /^\/login\/?$/.test(useLocation().pathname);
    return (
        <>
            {onLogin && <SiteHeader currentPath="/login" />}
            <Suspense fallback={<LoadingScreen />}>
                <CloudApp />
            </Suspense>
        </>
    );
}

// SSR-safe router. During SSG prerender there is no `document`, so BrowserRouter's
// createBrowserHistory throws -- use StaticRouter on the server and BrowserRouter on
// the client. The route comes from SsrRouteContext, set per-render by the
// ViteReactSSG fn callback in main.tsx via a ReactNode wrapper. Using React context
// (not a module-level variable) avoids a race condition with concurrent SSG tasks.
// `import.meta.env.SSR` is statically replaced by Vite so the unused branch
// (and its import) is tree-shaken from each bundle.
function AppRouter({ children }: { children: ReactNode }) {
    // Always call at top level -- hooks must not be called conditionally.
    // import.meta.env.SSR is a static build-time constant so this value is
    // unused (and tree-shaken) in the client bundle, but ESLint cannot know that.
    const ssrRoute = useContext(SsrRouteContext);
    if (import.meta.env.SSR) {
        return <StaticRouter location={ssrRoute}>{children}</StaticRouter>;
    }
    return <BrowserRouter>{children}</BrowserRouter>;
}

function AppRoutes() {
    return (
        <AppRouter>
            {!import.meta.env.SSR && <RedactedPageViews />}
            <div className="app">
                <ChunkErrorBoundary>
                <Suspense fallback={<LoadingScreen />}>
                    <Routes>
                        {/* Public pages - always accessible, no auth gate */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/bluebeam-for-mac" element={<BluebeamForMacPage />} />
                        <Route path="/bluebeam-revu-free-alternative" element={<BluebeamRevuFreeAlternativePage />} />
                        <Route path="/bluebeam-revu-20-end-of-life" element={<BluebeamRevu20EndOfLifePage />} />
                        <Route path="/measure-pdf-online" element={<MeasurePdfOnlinePage />} />
                        <Route path="/drawing-scales" element={<DrawingScalesPage />} />
                        <Route path="/measure-area-pdf" element={<MeasureAreaPdfPage />} />
                        <Route path="/calibrate-pdf-scale" element={<CalibratePdfScalePage />} />
                        <Route path="/share-pdf-drawings-for-review" element={<SharePdfDrawingsForReviewPage />} />
                        <Route path="/ipad-pdf-markup" element={<IpadPdfMarkupPage />} />
                        <Route path="/redline-pdf-drawing" element={<RedlinePdfDrawingPage />} />
                        <Route path="/free-pdf-markup-tools" element={<FreePdfMarkupToolsPage />} />
                        <Route path="/whats-new" element={<WhatsNewPage />} />
                        <Route path="/de" element={<DeLandingPage />} />
                        <Route path="/fr" element={<FrLandingPage />} />
                        <Route path="/de/pdf-messen" element={<DePdfMessenPage />} />
                        <Route path="/fr/mesurer-pdf" element={<FrMesurerPdfPage />} />
                        {/* /pricing is retired -- Stripe checkout is dormant; redirect to home */}
                        <Route path="/pricing" element={<Navigate to="/" replace />} />

                        {/* Local viewer: the opened PDF lives at its own URL
                            (/local/:slug). Bare /local has no document -> home. */}
                        <Route path="/local" element={<Navigate to="/" replace />} />
                        <Route path="/local/:slug" element={<LocalViewerPage />} />

                        {/* Hidden device probe, reached by URL only (like ?inkdebug=1): /probe/image */}
                        <Route path="/probe/image" element={<ImageProbePage />} />

                        <Route path="*" element={<CloudRoute />} />
                    </Routes>
                </Suspense>
                </ChunkErrorBoundary>
            </div>
        </AppRouter>
    );
}

function App() {
    // HelmetProvider is required so Head components in prerendered pages can
    // update <title>/<meta> on the client after hydration and in tests.
    // During SSG (import.meta.env.SSR = true) this wrapper is skipped so
    // SinglePageAdapter's outer HelmetProvider (with helmetContext) is the
    // only one -- nested providers would shadow the context used for extraction.
    const inner = (
        <ActorProvider>
            <FeedbackProvider>
                <AppRoutes />
                <EventInspector />
                <VersionBadge />
                <UpdateNotice />
            </FeedbackProvider>
        </ActorProvider>
    );
    if (import.meta.env.SSR) return inner;
    return <HelmetProvider>{inner}</HelmetProvider>;
}

export default App;
